import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useI18n } from '~/i18n/index.tsx';

/**
 * A note behind an "i" — ported from the reference panel's InfoHint, whose sheet is in
 * `styles/ref/InfoHint.css`.
 *
 * A line of guidance under every field is read once and then skipped for good, while costing the
 * form a line each time and pushing the fields apart. Behind the icon it is one hover, one tap or
 * one Tab away, and the form reads as a form.
 *
 * Nothing is lost to anyone who cannot see the bubble. The note is always in the page, visually
 * hidden, and `id` lets the field it explains point at it with `aria-describedby` — so a screen
 * reader hears it on reaching the field, as it did when the note was printed under it. The button
 * is described by the same note.
 *
 * The bubble is portalled and fixed-positioned, so a card's `overflow: hidden` cannot clip it. It
 * opens on hover and on focus, a click pins it open (which is how a touch screen opens it), and
 * Escape, scrolling or a press anywhere else closes it.
 */
export function InfoHint({ text, id }: { text: string; id?: string }) {
  const { t } = useI18n();
  const own = useId();
  const noteId = id ?? `${own}-note`;
  const trigger = useRef<HTMLButtonElement>(null);

  const [place, setPlace] = useState<{ left: number; top: number; above: boolean } | null>(null);
  const [pinned, setPinned] = useState(false);

  function show() {
    const rect = trigger.current!.getBoundingClientRect();
    const above = rect.top > 160;
    // Kept clear of the window's edges: the bubble is centred on the icon and up to 300px wide.
    const left = Math.min(Math.max(rect.left + rect.width / 2, 166), window.innerWidth - 166);

    setPlace({ left, top: above ? rect.top : rect.bottom, above });
  }

  function hide() {
    setPlace(null);
    setPinned(false);
  }

  useEffect(() => {
    if (!place) {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hide();
      }
    };
    const onPress = (event: PointerEvent) => {
      if (!trigger.current?.contains(event.target as Node)) {
        hide();
      }
    };

    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPress);

    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPress);
    };
  }, [place]);

  return (
    <span className="info-hint">
      <button
        ref={trigger}
        type="button"
        className="info-hint__trigger"
        aria-label={t('common.moreInfo')}
        aria-describedby={noteId}
        aria-expanded={place !== null}
        onMouseEnter={show}
        onMouseLeave={() => !pinned && setPlace(null)}
        onFocus={show}
        onBlur={hide}
        onClick={() => {
          if (pinned) {
            hide();
          } else {
            show();
            setPinned(true);
          }
        }}
      >
        <svg className="info-hint__glyph" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <circle className="info-hint__disc" cx="12" cy="12" r="10" />
          <circle className="info-hint__dot" cx="12" cy="7.7" r="1.4" />
          <path className="info-hint__stem" d="M12 11.2v5.6" />
        </svg>
      </button>

      <span className="sr-only" id={noteId}>
        {text}
      </span>

      {place
        ? createPortal(
            <span
              className={`info-hint__tip info-hint__tip--${place.above ? 'above' : 'below'}`}
              style={{ left: place.left, top: place.top }}
              // Already read out through the note above; announcing the bubble too would say it twice.
              aria-hidden="true"
            >
              {text}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

/**
 * A field's name, and its note behind an "i" when it has one.
 *
 * The note sits beside the label rather than inside it: a `<label>` names its control from
 * everything in it, and a button in there would add "more information" to the field's own name.
 */
export function FieldHead({
  htmlFor,
  label,
  hint,
  hintId,
  required = false,
}: {
  htmlFor?: string;
  label: string;
  hint?: string;
  hintId?: string;
  required?: boolean;
}) {
  const mark = required ? (
    <span className="required-mark" aria-hidden="true">
      *
    </span>
  ) : null;

  return (
    <span className="field__head">
      {htmlFor ? (
        <label htmlFor={htmlFor}>
          {label}
          {mark}
        </label>
      ) : (
        <span>
          {label}
          {mark}
        </span>
      )}
      {hint ? <InfoHint text={hint} id={hintId} /> : null}
    </span>
  );
}
